/**
 * NoteAndVisitConfigurator (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * Unified configurator embedded inside Clinical Preferences. Three modes:
 *   • Global  — one default note format applies to every visit type
 *   • Visit   — per-visit-type overrides (each can opt into the global
 *               default OR carry its own format + sections + rules)
 *   • Templates — quick-apply prebuilt scaffolds, optionally linked to a
 *               visit type so they auto-route on encounter open.
 *
 * Autosave: every edit goes to localStorage immediately. A "Saved ✓"
 * indicator flashes after each write (1.5s) so users have feedback
 * without "save" anxiety.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, ChevronRight, ChevronUp, GripVertical, Plus, Trash2,
  CheckCheck, Sparkles, Link2, RotateCcw, FilePlus, Save, Pencil,
  Users, Share2, Copy, Wand2, Calendar,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { useSpecialty } from '../../contexts/SpecialtyContext';
import {
  NOTE_FORMATS, noteFormatById, COPY_FORWARD_RULES,
  getGlobalConfig, saveGlobalConfig,
  getVisitConfig, saveVisitConfig, resetVisitConfig,
  buildDefaultConfig,
  reorderSections, renameSection, toggleSection, removeSection, addSection,
  addSubheading, renameSubheading, removeSubheading, reorderSubheadings,
  addAssessment, removeAssessment, renameAssessment,
  setPromptHint,
  getCustomTemplates, upsertCustomTemplate, deleteCustomTemplate,
  getOrgSharedTemplates, shareTemplateWithOrg, unshareTemplateFromOrg,
} from '../../lib/demo/noteConfigStore';
import {
  getStarterPackForSpecialty, cloneStarterPack,
} from '../../lib/demo/specialtyStarterPacks';
import { DEMO_VISIT_TYPES } from '../../lib/demo/noteFormatCatalog';

/* ─── Autosave indicator hook ─────────────────────────────────────── */
const useSavedFlash = () => {
  const [flash, setFlash] = useState(false);
  const tRef = useRef(null);
  const trigger = () => {
    setFlash(true);
    if (tRef.current) clearTimeout(tRef.current);
    tRef.current = setTimeout(() => setFlash(false), 1500);
  };
  useEffect(() => () => tRef.current && clearTimeout(tRef.current), []);
  return [flash, trigger];
};

const SavedIndicator = ({ flash, testId = 'saved-indicator' }) => (
  <span
    className={cn(
      'inline-flex items-center gap-1 text-[10px] font-medium tabular-nums transition-opacity',
      flash ? 'opacity-100 text-emerald-700' : 'opacity-50 text-slate-400',
    )}
    data-testid={testId}
    aria-live="polite"
  >
    <CheckCheck className="h-3 w-3" /> Saved
  </span>
);

/* ─── Tiny toggle ─────────────────────────────────────────────────── */
const Toggle = ({ on, onChange, testId, ariaLabel }) => (
  <button
    type="button"
    onClick={onChange}
    className={cn('h-4 w-7 rounded-full relative transition shrink-0', on ? 'bg-slate-900' : 'bg-slate-300')}
    style={{ border: 'none', cursor: 'pointer' }}
    data-testid={testId}
    aria-label={ariaLabel}
    aria-pressed={on}
  >
    <span className={cn('absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform', on ? 'translate-x-3.5' : 'translate-x-0.5')} />
  </button>
);

/* ─── Inline-editable label (click-to-edit) ───────────────────────── */
const InlineLabel = ({ value, onChange, className, testId, placeholder }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const commit = () => { setEditing(false); if (draft !== value && draft.trim()) onChange(draft.trim()); else setDraft(value); };
  if (editing) {
    return (
      <Input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(value); setEditing(false); } }}
        className={cn('h-6 text-[12px] py-0', className)}
        data-testid={`${testId}-input`}
        placeholder={placeholder}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={cn('text-left hover:bg-slate-100 rounded px-1 -mx-1 py-0.5 cursor-text', className)}
      style={{ background: 'none', border: 'none' }}
      data-testid={testId}
      title="Click to rename"
    >
      {value || <span className="text-slate-400 italic">{placeholder || 'Click to edit'}</span>}
    </button>
  );
};

/* ─── Note Format picker (9 cards) ────────────────────────────────── */
const NoteFormatPicker = ({ value, onChange, testIdPrefix = 'note-fmt' }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-2" data-testid={`${testIdPrefix}-picker`}>
    {NOTE_FORMATS.map((f) => {
      const selected = f.id === value;
      const sectionPreview = f.sections.slice(0, 4).map((s) => s.label).join(' · ') + (f.sections.length > 4 ? ' …' : '');
      return (
        <button
          key={f.id}
          type="button"
          onClick={() => onChange(f.id)}
          className={cn(
            'text-left rounded-lg border p-3 transition-all',
            selected ? 'border-[#6b5ce7] bg-violet-50 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300',
          )}
          style={{ cursor: 'pointer' }}
          data-testid={`${testIdPrefix}-${f.id}`}
        >
          <div className="flex items-center justify-between mb-1">
            <p className="text-[12px] font-semibold text-slate-800">{f.label}</p>
            {selected && <Badge className="text-[8px] bg-violet-100 text-violet-700 border-violet-200">Active</Badge>}
          </div>
          <p className="text-[10px] text-slate-500 leading-snug mb-1.5">{f.desc}</p>
          <p className="text-[9px] text-slate-400 truncate">{sectionPreview}</p>
        </button>
      );
    })}
  </div>
);

/* ─── Section editor (rename / add / remove / reorder / toggle / subheadings) */
const SectionEditor = ({ sections, onChange, testIdPrefix = 'sect' }) => {
  const [expanded, setExpanded] = useState(() => new Set());
  const ordered = useMemo(() => [...(sections || [])].sort((a, b) => a.order - b.order), [sections]);
  const toggleExp = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  const move = (idx, delta) => {
    const j = idx + delta;
    if (j < 0 || j >= ordered.length) return;
    onChange(reorderSections(ordered, idx, j));
  };

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`${testIdPrefix}-list`}>
      {ordered.map((s, i) => {
        const isOpen = expanded.has(s.id);
        return (
          <div key={s.id} className="border-b border-slate-100 last:border-b-0 bg-white" data-testid={`${testIdPrefix}-row-${s.id}`}>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5">
              <GripVertical className="h-3 w-3 text-slate-300 shrink-0" aria-hidden="true" />
              <div className="flex flex-col gap-0 shrink-0">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className={cn('h-3 w-4 inline-flex items-center justify-center text-slate-500', i === 0 && 'opacity-30 cursor-not-allowed')}
                  style={{ background: 'none', border: 'none' }}
                  aria-label={`Move ${s.label} up`}
                  data-testid={`${testIdPrefix}-up-${s.id}`}
                >
                  <ChevronUp className="h-2.5 w-2.5" />
                </button>
                <button
                  type="button"
                  onClick={() => move(i, +1)}
                  disabled={i === ordered.length - 1}
                  className={cn('h-3 w-4 inline-flex items-center justify-center text-slate-500', i === ordered.length - 1 && 'opacity-30 cursor-not-allowed')}
                  style={{ background: 'none', border: 'none' }}
                  aria-label={`Move ${s.label} down`}
                  data-testid={`${testIdPrefix}-down-${s.id}`}
                >
                  <ChevronDown className="h-2.5 w-2.5" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => toggleExp(s.id)}
                className="text-slate-400 hover:text-slate-700 shrink-0"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                aria-label={isOpen ? 'Collapse subheadings' : 'Expand subheadings'}
                data-testid={`${testIdPrefix}-expand-${s.id}`}
              >
                {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </button>
              <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500 tabular-nums shrink-0">#{i + 1}</Badge>
              <InlineLabel
                value={s.label}
                onChange={(v) => onChange(renameSection(ordered, s.id, v))}
                className="flex-1 text-[12px] font-medium text-slate-800"
                testId={`${testIdPrefix}-label-${s.id}`}
                placeholder="Section name"
              />
              {(s.subheadings || []).length > 0 && (
                <Badge className="text-[9px] bg-slate-100 text-slate-500 border-0 shrink-0">{s.subheadings.length} sub</Badge>
              )}
              {s.promptHint && (
                <Badge
                  className="text-[9px] bg-violet-50 text-violet-700 border border-violet-200 inline-flex items-center gap-0.5 shrink-0"
                  title={s.promptHint}
                  data-testid={`${testIdPrefix}-prompt-badge-${s.id}`}
                >
                  <Wand2 className="h-2 w-2" /> AI hint
                </Badge>
              )}
              <Toggle
                on={!!s.enabled}
                onChange={() => onChange(toggleSection(ordered, s.id))}
                testId={`${testIdPrefix}-toggle-${s.id}`}
                ariaLabel={`Enable section ${s.label}`}
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => onChange(removeSection(ordered, s.id))}
                aria-label={`Remove section ${s.label}`}
                data-testid={`${testIdPrefix}-remove-${s.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>

            {isOpen && (
              <div className="pl-12 pr-2.5 pb-2 space-y-2" data-testid={`${testIdPrefix}-subs-${s.id}`}>
                {/* AI prompt hint — single line, surfaced to the note generator */}
                <div className="flex items-center gap-1.5">
                  <Wand2 className="h-3 w-3 text-violet-500 shrink-0" />
                  <Input
                    value={s.promptHint || ''}
                    onChange={(e) => onChange(setPromptHint(ordered, s.id, e.target.value))}
                    placeholder={`AI prompt hint for ${s.label} (e.g. "Always include OLDCARTS")`}
                    maxLength={140}
                    className="h-6 text-[10px] py-0 placeholder:text-slate-300"
                    data-testid={`${testIdPrefix}-prompt-${s.id}`}
                  />
                  <span className="text-[9px] text-slate-400 tabular-nums shrink-0 w-12 text-right">
                    {(s.promptHint || '').length}/140
                  </span>
                </div>
                {(s.subheadings || []).map((sub, si) => (
                  <div key={sub.id} className="flex items-center gap-1.5">
                    <span className="text-[9px] text-slate-400 tabular-nums w-5 shrink-0">{si + 1}.</span>
                    <InlineLabel
                      value={sub.label}
                      onChange={(v) => onChange(renameSubheading(ordered, s.id, sub.id, v))}
                      className="flex-1 text-[11px] text-slate-700"
                      testId={`${testIdPrefix}-sub-label-${sub.id}`}
                      placeholder="Subheading"
                    />
                    <div className="flex flex-col gap-0">
                      <button
                        type="button"
                        onClick={() => onChange(reorderSubheadings(ordered, s.id, si, Math.max(0, si - 1)))}
                        disabled={si === 0}
                        className={cn('h-3 w-4 inline-flex items-center justify-center text-slate-400', si === 0 && 'opacity-30')}
                        style={{ background: 'none', border: 'none' }}
                        aria-label={`Move subheading ${sub.label} up`}
                        data-testid={`${testIdPrefix}-sub-up-${sub.id}`}
                      >
                        <ChevronUp className="h-2.5 w-2.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onChange(reorderSubheadings(ordered, s.id, si, Math.min(s.subheadings.length - 1, si + 1)))}
                        disabled={si === s.subheadings.length - 1}
                        className={cn('h-3 w-4 inline-flex items-center justify-center text-slate-400', si === s.subheadings.length - 1 && 'opacity-30')}
                        style={{ background: 'none', border: 'none' }}
                        aria-label={`Move subheading ${sub.label} down`}
                        data-testid={`${testIdPrefix}-sub-down-${sub.id}`}
                      >
                        <ChevronDown className="h-2.5 w-2.5" />
                      </button>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-5 w-5 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      onClick={() => onChange(removeSubheading(ordered, s.id, sub.id))}
                      aria-label={`Remove subheading ${sub.label}`}
                      data-testid={`${testIdPrefix}-sub-remove-${sub.id}`}
                    >
                      <Trash2 className="h-2.5 w-2.5" />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-[10px] text-slate-500 gap-1"
                  onClick={() => onChange(addSubheading(ordered, s.id))}
                  data-testid={`${testIdPrefix}-sub-add-${s.id}`}
                >
                  <Plus className="h-2.5 w-2.5" /> Add subheading
                </Button>
              </div>
            )}
          </div>
        );
      })}
      <div className="bg-slate-50 px-2.5 py-1.5">
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-[11px] gap-1 border-dashed"
          onClick={() => onChange(addSection(ordered))}
          data-testid={`${testIdPrefix}-add`}
        >
          <Plus className="h-3 w-3" /> Add section
        </Button>
      </div>
    </div>
  );
};

/* ─── Copy-forward + Assessments panel ────────────────────────────── */
const CopyAndAssessments = ({ sections, copyForward, assessments, onChangeCopy, onChangeAssessments, testIdPrefix = 'cfg' }) => {
  const ordered = useMemo(() => [...(sections || [])].sort((a, b) => a.order - b.order), [sections]);
  const seeds = assessments?.seeds || [];

  const setRule = (sectionId, rule) => {
    const nextRules = { ...(copyForward.rules || {}), [sectionId]: rule };
    const nextConditions = { ...(copyForward.conditions || {}) };
    // Seed a default condition the first time a section becomes 'conditional'.
    if (rule === 'conditional' && !nextConditions[sectionId]) {
      nextConditions[sectionId] = { value: 90, unit: 'days' };
    }
    onChangeCopy({ ...copyForward, rules: nextRules, conditions: nextConditions });
  };
  const setCondition = (sectionId, patch) => {
    const prev = copyForward.conditions?.[sectionId] || { value: 90, unit: 'days' };
    const next = { ...prev, ...patch };
    onChangeCopy({
      ...copyForward,
      conditions: { ...(copyForward.conditions || {}), [sectionId]: next },
    });
  };
  const setEnabled = (sectionId, on) => {
    onChangeCopy({ ...copyForward, sectionsEnabled: { ...(copyForward.sectionsEnabled || {}), [sectionId]: on } });
  };

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 overflow-hidden" data-testid={`${testIdPrefix}-cf-table`}>
        <table className="w-full text-[11px]">
          <thead className="bg-slate-50 text-[9px] text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="text-left px-2.5 py-1.5 font-semibold">Section</th>
              <th className="text-left px-2.5 py-1.5 font-semibold">Copy-forward rule</th>
              <th className="text-right px-2.5 py-1.5 font-semibold">Enabled</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {ordered.map((s) => {
              const rule = copyForward?.rules?.[s.id] || 'same-visit';
              const enabled = !!copyForward?.sectionsEnabled?.[s.id];
              const condition = copyForward?.conditions?.[s.id] || { value: 90, unit: 'days' };
              return (
                <tr key={s.id} data-testid={`${testIdPrefix}-cf-row-${s.id}`}>
                  <td className="px-2.5 py-1.5 font-medium text-slate-700 align-top">{s.label}</td>
                  <td className="px-2.5 py-1.5">
                    <Select value={rule} onValueChange={(v) => setRule(s.id, v)}>
                      <SelectTrigger className="h-6 text-[11px] w-48" data-testid={`${testIdPrefix}-cf-rule-${s.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COPY_FORWARD_RULES.map((r) => <SelectItem key={r.id} value={r.id} className="text-[11px]">{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {rule === 'conditional' && (
                      <div
                        className="mt-1.5 flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-violet-50 border border-violet-100"
                        data-testid={`${testIdPrefix}-cf-cond-${s.id}`}
                      >
                        <Calendar className="h-3 w-3 text-violet-600 shrink-0" />
                        <span className="text-[10px] text-slate-700">Copy only if last visit was within</span>
                        <Input
                          type="number"
                          min={1}
                          max={3650}
                          value={condition.value}
                          onChange={(e) => setCondition(s.id, { value: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                          className="h-6 w-14 text-[11px] tabular-nums py-0"
                          data-testid={`${testIdPrefix}-cf-cond-value-${s.id}`}
                        />
                        <Select value={condition.unit} onValueChange={(v) => setCondition(s.id, { unit: v })}>
                          <SelectTrigger className="h-6 text-[11px] w-24" data-testid={`${testIdPrefix}-cf-cond-unit-${s.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="days" className="text-[11px]">days</SelectItem>
                            <SelectItem value="weeks" className="text-[11px]">weeks</SelectItem>
                            <SelectItem value="months" className="text-[11px]">months</SelectItem>
                            <SelectItem value="years" className="text-[11px]">years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </td>
                  <td className="px-2.5 py-1.5 text-right align-top">
                    <Toggle on={enabled} onChange={() => setEnabled(s.id, !enabled)} testId={`${testIdPrefix}-cf-enabled-${s.id}`} ariaLabel={`Copy ${s.label}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-2.5 space-y-2" data-testid={`${testIdPrefix}-ap`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold text-slate-800">Assessment &amp; Plan blocks</p>
            <p className="text-[10px] text-slate-500">Seed N default A/P blocks; the composer can still add/remove them at write time.</p>
          </div>
          <Button size="sm" variant="outline" className="text-[11px] h-7 gap-1" onClick={() => onChangeAssessments({ seeds: addAssessment(seeds) })} data-testid={`${testIdPrefix}-ap-add`}>
            <Plus className="h-3 w-3" /> Add block
          </Button>
        </div>
        {seeds.length === 0 ? (
          <p className="text-[10px] text-slate-500">No seeds — composer will start with one empty A/P block.</p>
        ) : (
          <div className="space-y-1.5">
            {seeds.map((seed, i) => (
              <div key={seed.id} className="flex items-center gap-2" data-testid={`${testIdPrefix}-ap-row-${seed.id}`}>
                <Badge variant="outline" className="text-[9px] border-slate-200 text-slate-500 tabular-nums shrink-0">A/P #{i + 1}</Badge>
                <InlineLabel
                  value={seed.label}
                  onChange={(v) => onChangeAssessments({ seeds: renameAssessment(seeds, seed.id, v) })}
                  className="flex-1 text-[11px] text-slate-700"
                  testId={`${testIdPrefix}-ap-label-${seed.id}`}
                />
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => onChangeAssessments({ seeds: removeAssessment(seeds, seed.id) })} aria-label={`Remove ${seed.label}`} data-testid={`${testIdPrefix}-ap-remove-${seed.id}`}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Custom templates lightbox ───────────────────────────────────── */
const CustomTemplatesDialog = ({
  open, onOpenChange, customTemplates, orgSharedTemplates, currentUserId,
  onApplyCustom, onApplyPreloaded, onScratch,
  onRenameCustom, onDeleteCustom, onToggleShare, onCloneOrgShared, onSetDescription,
}) => {
  const preloaded = NOTE_FORMATS.filter((f) => f.id !== 'custom');
  // Org-shared library is everything shared by SOMEONE ELSE — your own
  // shared entries appear in "My custom templates" with a "Shared" badge.
  const teammateShared = (orgSharedTemplates || []).filter((t) => t.ownerId !== currentUserId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="custom-tpl-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px] inline-flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-600" /> Custom note templates
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Apply a saved custom template, clone one shared by your team, personalize a preloaded format, or start blank.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* My custom templates */}
          <section data-testid="custom-tpl-mine">
            <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mb-2">
              My custom templates
            </p>
            {customTemplates.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center">
                <p className="text-[11px] text-slate-500">
                  No saved templates yet. Personalize a preloaded format below, then click
                  &ldquo;Save as custom template&rdquo; on the editor.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 overflow-hidden divide-y divide-slate-100">
                {customTemplates.map((t) => (
                  <CustomTemplateRow
                    key={t.id}
                    tpl={t}
                    onApply={() => { onApplyCustom(t); onOpenChange(false); }}
                    onRename={(label) => onRenameCustom(t.id, label)}
                    onDelete={() => onDeleteCustom(t.id)}
                    onToggleShare={() => onToggleShare(t)}
                    onSetDescription={(d) => onSetDescription(t.id, d)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Shared by my team */}
          <section data-testid="custom-tpl-org">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide inline-flex items-center gap-1.5">
                <Users className="h-3 w-3" /> Shared by my team
              </p>
              <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                {teammateShared.length} available
              </Badge>
            </div>
            {teammateShared.length === 0 ? (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center">
                <p className="text-[11px] text-slate-500">
                  No team-shared templates yet. Toggle &ldquo;Share with org&rdquo; on one of your
                  templates to make it available to teammates.
                </p>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200 overflow-hidden divide-y divide-slate-100">
                {teammateShared.map((t) => (
                  <OrgSharedTemplateRow
                    key={t.id}
                    tpl={t}
                    onClone={() => { onCloneOrgShared(t); }}
                    onApply={() => { onApplyCustom(t); onOpenChange(false); }}
                  />
                ))}
              </div>
            )}
          </section>

          {/* Preloaded formats */}
          <section data-testid="custom-tpl-preloaded">
            <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide mb-2">
              Start from a preloaded template
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {preloaded.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => { onApplyPreloaded(f.id); onOpenChange(false); }}
                  className="text-left rounded-lg border border-slate-200 bg-white p-3 hover:border-[#6b5ce7] hover:bg-violet-50 transition-colors"
                  style={{ cursor: 'pointer' }}
                  data-testid={`custom-tpl-preloaded-${f.id}`}
                >
                  <div className="flex items-center justify-between mb-0.5">
                    <p className="text-[12px] font-semibold text-slate-800">{f.label}</p>
                    <Badge className="text-[8px] bg-slate-100 text-slate-500 border-0">
                      {f.sections.length} sec
                    </Badge>
                  </div>
                  <p className="text-[10px] text-slate-500 leading-snug line-clamp-2">{f.desc}</p>
                </button>
              ))}
            </div>
          </section>

          {/* From scratch */}
          <section data-testid="custom-tpl-scratch">
            <button
              type="button"
              onClick={() => { onScratch(); onOpenChange(false); }}
              className="w-full rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 hover:border-[#6b5ce7] hover:bg-violet-50 px-3 py-3 inline-flex items-center justify-center gap-2 transition-colors"
              style={{ cursor: 'pointer' }}
              data-testid="custom-tpl-scratch-btn"
            >
              <FilePlus className="h-4 w-4 text-slate-500" />
              <span className="text-[12px] font-semibold text-slate-700">Start from scratch</span>
              <span className="text-[10px] text-slate-500">— begin with one empty section</span>
            </button>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* Single row in the "My custom templates" list with click-to-rename + share toggle. */
const CustomTemplateRow = ({ tpl, onApply, onRename, onDelete, onToggleShare, onSetDescription }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tpl.label);
  const [editingDesc, setEditingDesc] = useState(false);
  const [draftDesc, setDraftDesc] = useState(tpl.description || '');
  useEffect(() => setDraft(tpl.label), [tpl.label]);
  useEffect(() => setDraftDesc(tpl.description || ''), [tpl.description]);
  const commit = () => { setEditing(false); if (draft.trim() && draft !== tpl.label) onRename(draft.trim()); else setDraft(tpl.label); };
  const commitDesc = () => {
    setEditingDesc(false);
    const trimmed = draftDesc.trim();
    if (trimmed !== (tpl.description || '')) onSetDescription(trimmed);
  };

  return (
    <div className="px-3 py-2 hover:bg-slate-50" data-testid={`custom-tpl-row-${tpl.id}`}>
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-violet-500 shrink-0" />
        <div className="flex-1 min-w-0">
          {editing ? (
            <Input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={commit}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(tpl.label); setEditing(false); } }}
              className="h-7 text-[12px]"
              data-testid={`custom-tpl-rename-${tpl.id}`}
            />
          ) : (
            <div className="flex items-center gap-1.5">
              <p className="text-[12px] font-medium text-slate-800 truncate">{tpl.label}</p>
              {tpl.sharedWithOrg && (
                <Badge className="text-[8px] bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-0.5">
                  <Share2 className="h-2 w-2" /> Shared
                </Badge>
              )}
            </div>
          )}
          <p className="text-[10px] text-slate-500 truncate">
            {(tpl.sections || []).length} section{(tpl.sections || []).length === 1 ? '' : 's'}
            {tpl.baseFormatId ? ` · based on ${noteFormatById(tpl.baseFormatId).label}` : ''}
          </p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className={cn(
            'h-7 w-7 p-0',
            tpl.sharedWithOrg ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-slate-500 hover:text-emerald-700 hover:bg-emerald-50',
          )}
          onClick={onToggleShare}
          aria-label={tpl.sharedWithOrg ? `Stop sharing ${tpl.label}` : `Share ${tpl.label} with org`}
          title={tpl.sharedWithOrg ? 'Stop sharing with org' : 'Share with org'}
          data-testid={`custom-tpl-share-${tpl.id}`}
        >
          <Share2 className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-500 hover:text-slate-800" onClick={() => setEditing(true)} aria-label={`Rename ${tpl.label}`} data-testid={`custom-tpl-rename-btn-${tpl.id}`}>
          <Pencil className="h-3 w-3" />
        </Button>
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={onDelete} aria-label={`Delete ${tpl.label}`} data-testid={`custom-tpl-delete-${tpl.id}`}>
          <Trash2 className="h-3 w-3" />
        </Button>
        <Button size="sm" className="h-7 text-[10px] gap-1 bg-[#6b5ce7] hover:bg-[#5849c7]" onClick={onApply} data-testid={`custom-tpl-apply-${tpl.id}`}>
          Apply
        </Button>
      </div>
      {/* Why this template? — one-line description, click to edit */}
      <div className="pl-5 pt-1">
        {editingDesc ? (
          <Input
            autoFocus
            value={draftDesc}
            onChange={(e) => setDraftDesc(e.target.value)}
            onBlur={commitDesc}
            onKeyDown={(e) => { if (e.key === 'Enter') commitDesc(); if (e.key === 'Escape') { setDraftDesc(tpl.description || ''); setEditingDesc(false); } }}
            placeholder="Why this template? (e.g. Use for new pediatric admissions)"
            maxLength={120}
            className="h-6 text-[10px]"
            data-testid={`custom-tpl-desc-input-${tpl.id}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setEditingDesc(true)}
            className={cn(
              'text-left w-full text-[10px] truncate hover:bg-slate-100 rounded px-1 -mx-1 py-0.5',
              tpl.description ? 'text-slate-600 italic' : 'text-slate-400 italic',
            )}
            style={{ background: 'none', border: 'none', cursor: 'text' }}
            data-testid={`custom-tpl-desc-${tpl.id}`}
            title="Click to edit description"
          >
            {tpl.description || '+ Add a one-liner: why this template?'}
          </button>
        )}
      </div>
    </div>
  );
};

/* Single row for org-shared templates owned by other providers. */
const OrgSharedTemplateRow = ({ tpl, onClone, onApply }) => (
  <div className="px-3 py-2 hover:bg-slate-50" data-testid={`org-shared-row-${tpl.id}`}>
    <div className="flex items-center gap-2">
      <Users className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-[12px] font-medium text-slate-800 truncate">{tpl.label}</p>
          <Badge className="text-[8px] bg-emerald-50 text-emerald-700 border border-emerald-200">
            Team
          </Badge>
        </div>
        <p className="text-[10px] text-slate-500 truncate">
          {tpl.ownerName || 'Teammate'} · {(tpl.sections || []).length} section{(tpl.sections || []).length === 1 ? '' : 's'}
          {tpl.baseFormatId ? ` · based on ${noteFormatById(tpl.baseFormatId).label}` : ''}
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="h-7 text-[10px] gap-1"
        onClick={onClone}
        data-testid={`org-shared-clone-${tpl.id}`}
      >
        <Copy className="h-3 w-3" /> Clone to mine
      </Button>
      <Button
        size="sm"
        className="h-7 text-[10px] gap-1 bg-[#6b5ce7] hover:bg-[#5849c7]"
        onClick={onApply}
        data-testid={`org-shared-apply-${tpl.id}`}
      >
        Apply
      </Button>
    </div>
    {tpl.description && (
      <p className="pl-5 pt-1 text-[10px] text-slate-600 italic truncate" data-testid={`org-shared-desc-${tpl.id}`} title={tpl.description}>
        {tpl.description}
      </p>
    )}
  </div>
);

/* ─── Save-as-custom dialog ───────────────────────────────────────── */
const SaveAsCustomDialog = ({ open, onOpenChange, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  useEffect(() => { if (open) { setName(''); setDescription(''); } }, [open]);
  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed, description.trim());
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="save-as-custom-dialog">
        <DialogHeader>
          <DialogTitle className="text-[14px] inline-flex items-center gap-2">
            <Save className="h-4 w-4 text-violet-600" /> Save as custom template
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Give this template a name. It will appear in the &ldquo;My custom templates&rdquo; list next time.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div>
            <label htmlFor="save-as-name" className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">Template name</label>
            <Input
              id="save-as-name"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) submit(); }}
              placeholder="e.g. My Cardiology Consult"
              className="h-9 text-[12px] mt-1"
              data-testid="save-as-custom-name"
            />
          </div>
          <div>
            <label htmlFor="save-as-desc" className="text-[10px] font-semibold text-slate-600 uppercase tracking-wide">
              Why this template? <span className="font-normal text-slate-400 normal-case">(optional, shown to teammates if shared)</span>
            </label>
            <Input
              id="save-as-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g. Use for new pediatric admissions"
              maxLength={120}
              className="h-9 text-[12px] mt-1"
              data-testid="save-as-custom-desc"
            />
            <p className="text-[9px] text-slate-400 mt-0.5 tabular-nums">{description.length}/120</p>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button size="sm" variant="outline" className="text-[11px] h-8" onClick={() => onOpenChange(false)} data-testid="save-as-custom-cancel">
              Cancel
            </Button>
            <Button size="sm" className="text-[11px] h-8 bg-[#111] hover:bg-[#222]" onClick={submit} disabled={!name.trim()} data-testid="save-as-custom-confirm">
              <Save className="h-3 w-3 mr-1" /> Save template
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ═══ Top-level component ══════════════════════════════════════════════ */
const NoteAndVisitConfigurator = () => {
  const auth = useAuth();
  const currentUserId = auth?.user?.id || 'demo-user';
  const currentUserName = auth?.user?.name || 'Me';
  const { specialty } = useSpecialty();
  const starterPackEntry = getStarterPackForSpecialty(specialty?.id);

  const [tab, setTab] = useState('global'); // 'global' | 'visit'
  const [global, setGlobal] = useState(getGlobalConfig);
  const [visitMap, setVisitMap] = useState(() => {
    const m = {};
    for (const v of DEMO_VISIT_TYPES) m[v.id] = getVisitConfig(v.id);
    return m;
  });
  const [activeVisitId, setActiveVisitId] = useState(DEMO_VISIT_TYPES[0].id);
  const [customTemplates, setCustomTemplates] = useState(getCustomTemplates);
  const [orgSharedTemplates, setOrgSharedTemplates] = useState(getOrgSharedTemplates);
  const [savedFlash, flashSaved] = useSavedFlash();
  // Per-specialty banner dismissal — sticky for the session.
  const [dismissedPackFor, setDismissedPackFor] = useState(null);

  // Lightbox state — `customScope` tells us whether the user clicked Custom
  // on the Global tab or the Per-visit-type tab so we apply to the right place.
  const [customDialog, setCustomDialog] = useState({ open: false, scope: 'global' });
  const [saveDialog, setSaveDialog] = useState({ open: false, scope: 'global' });

  /* Global ─────────────────── */
  const updateGlobal = (patch) => {
    const next = { ...global, ...patch };
    setGlobal(next);
    saveGlobalConfig(next);
    flashSaved();
  };
  const setGlobalFormat = (formatId) => {
    if (formatId === 'custom') {
      setCustomDialog({ open: true, scope: 'global' });
      return;
    }
    updateGlobal(buildDefaultConfig(formatId));
  };

  /* Visit ──────────────────── */
  const activeVisit = DEMO_VISIT_TYPES.find((v) => v.id === activeVisitId);
  const activeCfg = visitMap[activeVisitId] || getVisitConfig(activeVisitId);
  const updateVisit = (patch) => {
    const next = { ...activeCfg, ...patch };
    const map = { ...visitMap, [activeVisitId]: next };
    setVisitMap(map);
    saveVisitConfig(activeVisitId, next);
    flashSaved();
  };
  const setVisitFormat = (formatId) => {
    if (formatId === 'custom') {
      setCustomDialog({ open: true, scope: 'visit' });
      return;
    }
    updateVisit({ ...buildDefaultConfig(formatId), useGlobalDefault: false });
  };
  const setVisitUseGlobal = (use) => {
    if (use) {
      const next = { useGlobalDefault: true, ...buildDefaultConfig(global.formatId) };
      const map = { ...visitMap, [activeVisitId]: next };
      setVisitMap(map);
      saveVisitConfig(activeVisitId, next);
    } else {
      updateVisit({ useGlobalDefault: false });
    }
    flashSaved();
  };
  const resetVisit = () => {
    resetVisitConfig(activeVisitId);
    const next = getVisitConfig(activeVisitId);
    setVisitMap({ ...visitMap, [activeVisitId]: next });
    flashSaved();
  };

  // The "effective" config to show / edit for a visit type.
  const effectiveCfg = activeCfg.useGlobalDefault
    ? { ...global, useGlobalDefault: true }
    : activeCfg;

  /* Custom templates ───────── */
  const applyCustomToScope = (cfg) => {
    if (customDialog.scope === 'global') {
      updateGlobal(cfg);
    } else {
      updateVisit({ ...cfg, useGlobalDefault: false });
    }
  };
  const handleApplyCustom = (tpl) => {
    // Deep-clone to avoid live-mutating the saved template.
    const cloned = {
      formatId: 'custom',
      sections: (tpl.sections || []).map((s, i) => ({ ...s, order: i, subheadings: (s.subheadings || []).map((x) => ({ ...x })) })),
      copyForward: { ...(tpl.copyForward || { sectionsEnabled: {}, rules: {} }) },
      assessments: { seeds: [...((tpl.assessments && tpl.assessments.seeds) || [])] },
    };
    applyCustomToScope(cloned);
  };
  const handleApplyPreloaded = (formatId) => {
    // Use the chosen format's section layout but mark formatId as 'custom' so
    // the user can freely rename / reorder without it looking misleading.
    const base = buildDefaultConfig(formatId);
    applyCustomToScope({ ...base, formatId: 'custom' });
  };
  const handleScratch = () => {
    applyCustomToScope(buildDefaultConfig('custom'));
  };
  const handleRenameCustom = (id, label) => {
    const all = customTemplates.map((t) => (t.id === id ? { ...t, label } : t));
    setCustomTemplates(all);
    upsertCustomTemplate(all.find((t) => t.id === id));
    // Keep the org-library entry in sync if it was shared.
    const owned = all.find((t) => t.id === id);
    if (owned?.sharedWithOrg) {
      setOrgSharedTemplates(shareTemplateWithOrg(owned));
    }
    flashSaved();
  };
  const handleDeleteCustom = (id) => {
    const next = deleteCustomTemplate(id);
    setCustomTemplates(next);
    setOrgSharedTemplates(getOrgSharedTemplates());
    flashSaved();
  };
  const handleSaveAsCustom = (label, description = '') => {
    const source = saveDialog.scope === 'global' ? global : effectiveCfg;
    const tpl = {
      id: `tpl-${Date.now()}`,
      label,
      description,
      baseFormatId: source.formatId === 'custom' ? null : source.formatId,
      sections: (source.sections || []).map((s) => ({ ...s, subheadings: (s.subheadings || []).map((x) => ({ ...x })) })),
      copyForward: { ...(source.copyForward || {}) },
      assessments: { seeds: [...((source.assessments && source.assessments.seeds) || [])] },
      ownerId: currentUserId,
      ownerName: currentUserName,
      sharedWithOrg: false,
      createdAt: new Date().toISOString(),
    };
    const all = upsertCustomTemplate(tpl);
    setCustomTemplates(all);
    flashSaved();
  };
  const handleToggleShare = (tpl) => {
    const nextShared = !tpl.sharedWithOrg;
    const updated = { ...tpl, sharedWithOrg: nextShared, ownerId: tpl.ownerId || currentUserId, ownerName: tpl.ownerName || currentUserName };
    const all = upsertCustomTemplate(updated);
    setCustomTemplates(all);
    if (nextShared) {
      setOrgSharedTemplates(shareTemplateWithOrg(updated));
    } else {
      setOrgSharedTemplates(unshareTemplateFromOrg(updated.id));
    }
    flashSaved();
  };
  const handleCloneOrgShared = (tpl) => {
    const cloneLabel = `${tpl.label} (copy)`;
    const cloned = {
      id: `tpl-${Date.now()}`,
      label: cloneLabel,
      description: tpl.description || '',
      baseFormatId: tpl.baseFormatId || null,
      sections: (tpl.sections || []).map((s, i) => ({ ...s, order: i, subheadings: (s.subheadings || []).map((x) => ({ ...x })) })),
      copyForward: { ...(tpl.copyForward || {}) },
      assessments: { seeds: [...((tpl.assessments && tpl.assessments.seeds) || [])] },
      ownerId: currentUserId,
      ownerName: currentUserName,
      sharedWithOrg: false,
      clonedFromId: tpl.id,
      clonedFromOwner: tpl.ownerName,
      createdAt: new Date().toISOString(),
    };
    const all = upsertCustomTemplate(cloned);
    setCustomTemplates(all);
    flashSaved();
  };
  const handleSetDescription = (id, description) => {
    const all = customTemplates.map((t) => (t.id === id ? { ...t, description } : t));
    setCustomTemplates(all);
    upsertCustomTemplate(all.find((t) => t.id === id));
    const owned = all.find((t) => t.id === id);
    if (owned?.sharedWithOrg) {
      setOrgSharedTemplates(shareTemplateWithOrg(owned));
    }
    flashSaved();
  };

  /* Render ─────────────────── */
  const isGlobalCustom = global.formatId === 'custom';
  const isVisitCustom = !activeCfg.useGlobalDefault && (activeCfg.formatId === 'custom');

  /* Specialty starter pack ─ */
  const applyStarterPack = () => {
    if (!starterPackEntry) return;
    const cfg = cloneStarterPack(starterPackEntry.pack);
    updateGlobal(cfg);
    setDismissedPackFor(specialty.id);
  };
  // Show the banner only if (a) we have a pack for this specialty AND
  // (b) the user hasn't already applied it (rough heuristic: format mismatch
  // OR pack section count differs) AND (c) they haven't dismissed it.
  const showStarterPackBanner = !!starterPackEntry
    && dismissedPackFor !== specialty?.id
    && (
      global.formatId !== starterPackEntry.pack.formatId
      || (global.sections || []).length !== starterPackEntry.pack.sections.length
    );

  return (
    <section className="space-y-3" data-testid="note-and-visit-configurator">
      {/* Tabs + saved indicator */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-200">
        <div className="flex items-center gap-0.5">
          {[
            { id: 'global', label: 'Global default', icon: Sparkles },
            { id: 'visit',  label: 'Per visit type', icon: Link2 },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={cn('px-3 py-2 text-[11px] font-medium transition-colors inline-flex items-center gap-1.5', active ? 'text-slate-900 border-b-2 border-[#6b5ce7] -mb-px' : 'text-slate-500 hover:text-slate-700')}
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                data-testid={`note-cfg-tab-${t.id}`}
              >
                <Icon className="h-3 w-3" /> {t.label}
              </button>
            );
          })}
        </div>
        <SavedIndicator flash={savedFlash} testId="note-cfg-saved" />
      </div>

      {/* ── GLOBAL tab ─────────────────────────────────────────── */}
      {tab === 'global' && (
        <div className="space-y-3" data-testid="note-cfg-global">
          {showStarterPackBanner && (
            <div
              className="rounded-lg border border-violet-200 bg-violet-50 p-3 flex items-start gap-3"
              data-testid="starter-pack-banner"
            >
              <div className="h-7 w-7 rounded-full bg-violet-100 flex items-center justify-center shrink-0">
                <Wand2 className="h-3.5 w-3.5 text-violet-700" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] font-semibold text-slate-800">
                  {starterPackEntry.label} starter pack available
                </p>
                <p className="text-[10px] text-slate-600 mt-0.5">
                  One-click swap to {noteFormatById(starterPackEntry.pack.formatId).label} layout
                  with {starterPackEntry.label}-tuned sections + AI prompt hints.
                  Replaces your current Global default.
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-[10px] text-slate-500"
                  onClick={() => setDismissedPackFor(specialty.id)}
                  data-testid="starter-pack-dismiss"
                >
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  className="h-7 text-[10px] gap-1 bg-[#6b5ce7] hover:bg-[#5849c7]"
                  onClick={applyStarterPack}
                  data-testid="starter-pack-apply"
                >
                  <Wand2 className="h-3 w-3" /> Apply pack
                </Button>
              </div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <p className="text-[11px] font-semibold text-slate-700">Note Format</p>
              {isGlobalCustom && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[10px] gap-1"
                  onClick={() => setSaveDialog({ open: true, scope: 'global' })}
                  data-testid="global-save-as-custom"
                >
                  <Save className="h-3 w-3" /> Save as custom template
                </Button>
              )}
            </div>
            <NoteFormatPicker value={global.formatId} onChange={setGlobalFormat} testIdPrefix="global-fmt" />
          </div>
          <details open className="group">
            <summary className="text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1.5" data-testid="global-sections-summary">
              <ChevronDown className="h-3 w-3 group-open:rotate-0 -rotate-90 transition-transform" /> Sections &amp; subheadings
            </summary>
            <div className="mt-2">
              <SectionEditor
                sections={global.sections}
                onChange={(s) => updateGlobal({ sections: s })}
                testIdPrefix="global-sect"
              />
            </div>
          </details>
          <details className="group">
            <summary className="text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1.5" data-testid="global-cf-summary">
              <ChevronRight className="h-3 w-3 group-open:rotate-90 transition-transform" /> Copy-forward &amp; Assessment blocks
            </summary>
            <div className="mt-2">
              <CopyAndAssessments
                sections={global.sections}
                copyForward={global.copyForward}
                assessments={global.assessments}
                onChangeCopy={(c) => updateGlobal({ copyForward: c })}
                onChangeAssessments={(a) => updateGlobal({ assessments: a })}
                testIdPrefix="global"
              />
            </div>
          </details>
        </div>
      )}

      {/* ── VISIT TYPE tab ──────────────────────────────────────── */}
      {tab === 'visit' && (
        <div className="flex flex-col lg:flex-row gap-3" data-testid="note-cfg-visit">
          <aside className="w-full lg:w-52 shrink-0">
            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Visit types</p>
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              {DEMO_VISIT_TYPES.map((vt) => {
                const cfg = visitMap[vt.id];
                const usingGlobal = cfg?.useGlobalDefault;
                const fmt = noteFormatById((usingGlobal ? global.formatId : cfg?.formatId) || 'soap');
                const selected = vt.id === activeVisitId;
                return (
                  <button
                    key={vt.id}
                    type="button"
                    onClick={() => setActiveVisitId(vt.id)}
                    className={cn('w-full text-left px-2.5 py-1.5 border-b border-slate-100 last:border-b-0 transition-colors', selected ? 'bg-violet-50' : 'hover:bg-slate-50')}
                    style={{ borderLeft: selected ? '3px solid #6b5ce7' : '3px solid transparent', cursor: 'pointer' }}
                    data-testid={`note-cfg-vt-${vt.id}`}
                  >
                    <div className="flex items-center justify-between gap-1.5">
                      <span className={cn('text-[11px] font-medium', selected ? 'text-slate-900' : 'text-slate-700')}>{vt.name}</span>
                      <Badge className={cn('text-[8px] border-0', usingGlobal ? 'bg-slate-100 text-slate-500' : 'bg-violet-100 text-violet-700')}>{usingGlobal ? 'Global' : fmt.label}</Badge>
                    </div>
                    <p className="text-[9px] text-slate-400 mt-0.5">{vt.duration}m</p>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="flex-1 min-w-0 space-y-3">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-[12px] font-semibold text-slate-800">{activeVisit.name}</p>
                <p className="text-[10px] text-slate-500">
                  {activeCfg.useGlobalDefault
                    ? `Inheriting Global Default (${noteFormatById(global.formatId).label}). Toggle off to customize.`
                    : `Custom format: ${noteFormatById(activeCfg.formatId || 'soap').label}.`}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-slate-600">Use Global Default</span>
                <Toggle
                  on={!!activeCfg.useGlobalDefault}
                  onChange={() => setVisitUseGlobal(!activeCfg.useGlobalDefault)}
                  testId={`vt-${activeVisitId}-global-toggle`}
                  ariaLabel="Use global default for this visit type"
                />
                {!activeCfg.useGlobalDefault && (
                  <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={resetVisit} data-testid={`vt-${activeVisitId}-reset`}>
                    <RotateCcw className="h-2.5 w-2.5" /> Reset
                  </Button>
                )}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <p className="text-[11px] font-semibold text-slate-700">Note Format</p>
                {isVisitCustom && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-[10px] gap-1"
                    onClick={() => setSaveDialog({ open: true, scope: 'visit' })}
                    data-testid={`vt-${activeVisitId}-save-as-custom`}
                  >
                    <Save className="h-3 w-3" /> Save as custom template
                  </Button>
                )}
              </div>
              <NoteFormatPicker
                value={effectiveCfg.formatId}
                onChange={(id) => setVisitFormat(id)}
                testIdPrefix={`vt-${activeVisitId}-fmt`}
              />
            </div>

            <details open className="group">
              <summary className="text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1.5" data-testid={`vt-${activeVisitId}-sections-summary`}>
                <ChevronDown className="h-3 w-3" /> Sections &amp; subheadings
                {activeCfg.useGlobalDefault && <Badge className="ml-1 text-[8px] bg-slate-100 text-slate-500 border-0">Inherited (read-only)</Badge>}
              </summary>
              <div className={cn('mt-2', activeCfg.useGlobalDefault && 'pointer-events-none opacity-60')}>
                <SectionEditor
                  sections={effectiveCfg.sections}
                  onChange={(s) => !activeCfg.useGlobalDefault && updateVisit({ sections: s })}
                  testIdPrefix={`vt-${activeVisitId}-sect`}
                />
              </div>
            </details>

            <details className="group">
              <summary className="text-[11px] font-semibold text-slate-700 cursor-pointer select-none flex items-center gap-1.5" data-testid={`vt-${activeVisitId}-cf-summary`}>
                <ChevronRight className="h-3 w-3" /> Copy-forward &amp; Assessment blocks
              </summary>
              <div className={cn('mt-2', activeCfg.useGlobalDefault && 'pointer-events-none opacity-60')}>
                <CopyAndAssessments
                  sections={effectiveCfg.sections}
                  copyForward={effectiveCfg.copyForward}
                  assessments={effectiveCfg.assessments}
                  onChangeCopy={(c) => !activeCfg.useGlobalDefault && updateVisit({ copyForward: c })}
                  onChangeAssessments={(a) => !activeCfg.useGlobalDefault && updateVisit({ assessments: a })}
                  testIdPrefix={`vt-${activeVisitId}`}
                />
              </div>
            </details>
          </div>
        </div>
      )}

      {/* ── Custom templates lightbox ───────────────────────────── */}
      <CustomTemplatesDialog
        open={customDialog.open}
        onOpenChange={(o) => setCustomDialog((d) => ({ ...d, open: o }))}
        customTemplates={customTemplates}
        orgSharedTemplates={orgSharedTemplates}
        currentUserId={currentUserId}
        onApplyCustom={handleApplyCustom}
        onApplyPreloaded={handleApplyPreloaded}
        onScratch={handleScratch}
        onRenameCustom={handleRenameCustom}
        onDeleteCustom={handleDeleteCustom}
        onToggleShare={handleToggleShare}
        onCloneOrgShared={handleCloneOrgShared}
        onSetDescription={handleSetDescription}
      />

      {/* ── Save-as dialog ──────────────────────────────────────── */}
      <SaveAsCustomDialog
        open={saveDialog.open}
        onOpenChange={(o) => setSaveDialog((d) => ({ ...d, open: o }))}
        onSave={handleSaveAsCustom}
      />
    </section>
  );
};

export default NoteAndVisitConfigurator;
